'use client';

import { notFoundPathName, selectedLocaleLocalStorageKey } from '@/constants';
import { locales } from '@/i18n/locale-config';
import { usePathname, useRouter } from '@/i18n/navigation';
import { useClickOutside } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import { useLocale } from 'next-intl';
import { useParams } from 'next/navigation';
import { useEffect, useState, useTransition } from 'react';
import ChevronIcon from '../ui/ChevronIcon';

export default function LocaleSwitcher({
    className,
    color,
}: {
    className?: string;
    color: 'black' | 'white';
}) {
    const router = useRouter();
    const pathname = usePathname();
    const params = useParams();
    const currentLocale = useLocale();

    const handleClickOutside = () => setIsOpen(false);

    const selectorRef = useClickOutside<HTMLDivElement>(handleClickOutside);

    const isBlack = color === 'black';

    const [isOpen, setIsOpen] = useState(false);
    const [isPending, startTransition] = useTransition();

    const toggleSwitcher = () => setIsOpen((prev) => !prev);

    function onSelectChange(nextLocale: string) {
        startTransition(() => {
            // @ts-expect-error -- TypeScript will validate that only known `params`
            // are used in combination with a given `pathname`. Since the two will
            // always match for the current route, we can skip runtime checks.
            router.replace({ pathname, params }, { locale: nextLocale });
        });
    }

    useEffect(() => {
        if (pathname !== notFoundPathName)
            localStorage.setItem(selectedLocaleLocalStorageKey, currentLocale);
    }, [currentLocale, pathname]);

    return (
        <div
            className={cn(
                'relative inline-block text-left z-20',
                isPending && 'transition-opacity [&:disabled]:opacity-30',
                isBlack ? 'text-black' : 'text-tofu',
                className,
            )}
            ref={selectorRef}
        >
            <button
                type="button"
                aria-label="Language"
                className="items-center justify-center border-none bg-none lg:flex hidden"
                onClick={toggleSwitcher}
            >
                <span
                    className={cn(
                        'uppercase text-[26px] font-light font-helveticaNeue hover:text-blue-200 transition-colors duration-300',
                        isOpen && 'text-blue-200',
                    )}
                >
                    {currentLocale}
                </span>
                <span
                    className={cn(
                        'ml-2 max-lg:text-sm lg:text-p transition-transform duration-300',
                        isBlack ? 'text-black' : 'text-tofu',
                        !isOpen && 'rotate-180',
                    )}
                >
                    <ChevronIcon color={color} />
                </span>
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-fit origin-top-left z-20">
                    <div
                        className="py-1 flex flex-col max-lg:gap-3 lg:gap-5"
                        role="menu"
                        aria-orientation="vertical"
                        aria-labelledby="options-menu"
                    >
                        {locales.map((locale) => {
                            const isCurrentLocale = locale === currentLocale;

                            return (
                                <button
                                    type="button"
                                    className={cn(
                                        'group uppercase hover:text-blue-200 text-[26px] font-light font-helveticaNeue leading-none text-nowrap transition-colors duration-300',
                                        isCurrentLocale && 'text-blue-200',
                                    )}
                                    onClick={() => onSelectChange(locale)}
                                    key={locale}
                                >
                                    {locale}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="hidden max-lg:flex gap-5">
                {locales.map((locale) => {
                    const isCurrentLocale = locale === currentLocale;

                    return (
                        <button
                            type="button"
                            className={cn(
                                'group uppercase text-white text-[26px] font-light font-helveticaNeue leading-none text-nowrap transition-colors duration-300',
                                isCurrentLocale && 'opacity-40',
                            )}
                            onClick={() => onSelectChange(locale)}
                            key={locale}
                        >
                            {locale}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
